import React, { useEffect, useMemo, useState } from 'react';
import { Bot, Check, FileStack, Globe2, Landmark, RotateCcw, Send, UserCheck } from 'lucide-react';
import useInView from './useInView';
import useFunderCoverage, { plansWithOwnRules, type PlanLayers } from '@/hooks/useFunderCoverage';

/**
 * Plan by plan — the funder interface being built, replayed from real data.
 *
 * /api/public/funder-coverage returns every mapped plan, anonymised (state and
 * counts, no names), in the order its rules arrived. This replays them one at
 * a time: each plan gains its layers — the directory entry, the generic rows
 * every plan gets, its state's benefit rules, the rules that are its own —
 * and the counters on the right add exactly that plan's numbers. When the
 * replay ends they equal the live totals, then the queue shows what the agents
 * build next. Nothing here is invented: an empty layer says so ("None yet").
 *
 * Reduced motion shows the finished state. Without the build list (an older
 * backend) it shows the totals alone.
 */

const STEP_MS = 190;

const drafted = (p: PlanLayers) => p.drafted ?? 0;
const hasOwnRules = (p: PlanLayers) => p.plan_specific > 0 || drafted(p) > 0;
const STEPS = 5;

interface StepDef {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  detail: (p: PlanLayers) => string;
  empty?: (p: PlanLayers) => boolean;
  who: 'agent' | 'person' | 'both';
}

const STEP_DEFS: StepDef[] = [
  {
    icon: Globe2,
    title: 'Found in the national directory',
    detail: (p) => (p.has_edi ? 'Payer ID, states served and the transactions it accepts' : 'Directory entry; payer ID still to confirm'),
    who: 'agent',
  },
  {
    icon: FileStack,
    title: 'Generic rules',
    detail: (p) => `${p.generic} rows — filing window, channel, national add-on codes`,
    empty: (p) => p.generic === 0,
    who: 'agent',
  },
  {
    icon: Landmark,
    title: 'State benefit rules',
    detail: (p) => `${p.statewide} rows — ${p.state || 'state'} doula and CHW rules`,
    empty: (p) => p.statewide === 0,
    who: 'agent',
  },
  {
    icon: Bot,
    title: 'Plan-specific rules',
    detail: (p) =>
      p.plan_specific > 0
        ? `${p.plan_specific} rules this plan alone requires — drafted by an agent, approved by a person`
        : drafted(p) > 0
          ? `${drafted(p)} rules drafted by an agent from the plan’s manual — awaiting a person’s approval`
          : 'None yet — next for the agents to draft from the plan’s manual',
    empty: (p) => p.plan_specific === 0 && drafted(p) === 0,
    who: 'both',
  },
  {
    icon: Send,
    title: 'Ready to submit',
    detail: () => 'Claims and files go out the plan’s own way',
    who: 'agent',
  },
];

const n = (v: number) => v.toLocaleString();

const Counter: React.FC<{ value: string; label: string; sub?: string; strong?: boolean }> = ({ value, label, sub, strong }) => (
  <div className="py-3 border-b last:border-0 border-v2border">
    <div className={`font-bold tabular-nums ${strong ? 'text-4xl text-ink' : 'text-3xl text-ink'}`}>{value}</div>
    <div className="text-sm text-ink-700">{label}</div>
    {sub && <div className="text-xs text-ink-500 mt-0.5">{sub}</div>}
  </div>
);

const InterfaceBuilder: React.FC = () => {
  const { coverage, live } = useFunderCoverage();
  const build = useMemo(() => coverage.build ?? [], [coverage.build]);
  const total = build.length;
  const { ref, inView, reduced } = useInView<HTMLDivElement>(0.25);

  // tick counts steps across the whole replay: plan i, step s => i * STEPS + s
  const [tick, setTick] = useState(0);
  const [run, setRun] = useState(0);
  const end = total * STEPS;

  useEffect(() => {
    if (!inView || total === 0) return;
    if (reduced) {
      setTick(end);
      return;
    }
    setTick(0);
    const id = window.setInterval(() => {
      setTick((t) => {
        if (t >= end) {
          window.clearInterval(id);
          return t;
        }
        return t + 1;
      });
    }, STEP_MS);
    return () => window.clearInterval(id);
  }, [inView, reduced, total, end, run]);

  const done = Math.min(total, Math.floor(tick / STEPS));
  const current = Math.min(total - 1, done);
  const stepInPlan = tick >= end ? STEPS : tick % STEPS;
  const plan = build[current];
  const built = build.slice(0, done);
  const finished = total > 0 && tick >= end;

  const sums = built.reduce(
    (a, p) => ({
      rows: a.rows + p.rule_rows,
      specific: a.specific + (hasOwnRules(p) ? 1 : 0),
      drafts: a.drafts + (p.plan_specific === 0 && drafted(p) > 0 ? 1 : 0),
      states: p.state ? a.states.add(p.state) : a.states,
    }),
    { rows: 0, specific: 0, drafts: 0, states: new Set<string>() },
  );

  // Without the build list, fall back to the live totals alone.
  const mp = coverage.mapped_plans;
  const plansValue = total ? `${n(done)} / ${n(total)}` : n(mp.count);
  const rowsValue = total ? n(sums.rows) : n(mp.rule_rows);
  const specificValue = total ? n(sums.specific) : n(plansWithOwnRules(mp));
  const draftsSoFar = total ? sums.drafts : (mp.with_drafts ?? 0);
  const statesValue = total ? n(sums.states.size) : n(mp.states.length);
  const queue = coverage.queue;

  return (
    <section id="interface-builder" className="py-16 bg-paper-2 border-y border-v2border">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-sm font-semibold tracking-widest text-v2accent uppercase mb-2">Built plan by plan</p>
        <h2 className="text-2xl md:text-3xl font-bold text-ink mb-3">Agents build each funder’s interface. A person signs off.</h2>
        <p className="text-ink-500 max-w-3xl mb-8">
          Every plan is a directory entry, the rules every plan shares, its state’s benefit rules and the rules that are its
          own. Watch the platform’s own records replay, plan by plan; the counts on the right are live.
        </p>

        <div ref={ref} className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6">
          <div className="rounded-2xl border border-v2border bg-surface p-6">
            {plan ? (
              <>
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-ink-500">
                      Plan {n(current + 1)} of {n(total)}
                    </p>
                    <p className="text-xl font-bold text-ink">Medicaid plan · {plan.state || '—'}</p>
                  </div>
                  <span
                    className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${
                      finished || stepInPlan >= STEPS
                        ? 'bg-v2accent-soft text-v2accent-ink border-v2accent'
                        : 'bg-paper text-ink-700 border-v2border'
                    }`}
                  >
                    {finished || stepInPlan >= STEPS ? 'Live' : 'Building'}
                  </span>
                </div>
                <ol className="space-y-2.5" aria-live="polite">
                  {STEP_DEFS.map((s, i) => {
                    const reached = finished || i < stepInPlan;
                    const active = !finished && i === stepInPlan;
                    const isEmpty = s.empty?.(plan) ?? false;
                    return (
                      <li
                        key={s.title}
                        className={`flex items-start gap-3 rounded-xl border px-4 py-3 transition-all duration-300 ${
                          reached
                            ? isEmpty
                              ? 'border-dashed border-v2border bg-paper'
                              : 'border-v2accent bg-v2accent-soft'
                            : active
                              ? 'border-ink-400 bg-paper'
                              : 'border-v2border bg-paper opacity-50'
                        }`}
                      >
                        <span className="mt-0.5 w-7 h-7 shrink-0 rounded-lg bg-surface border border-v2border flex items-center justify-center">
                          {reached && !isEmpty ? (
                            <Check className="w-4 h-4 text-v2accent" aria-hidden="true" />
                          ) : (
                            <s.icon className="w-4 h-4 text-ink-500" aria-hidden="true" />
                          )}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-semibold text-ink">{s.title}</p>
                            {s.who !== 'agent' && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-ink-700">
                                <UserCheck className="w-3.5 h-3.5" aria-hidden="true" />
                                Person approves
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-ink-500">{reached || active ? s.detail(plan) : ' '}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <div className="mt-6">
                  <div className="flex flex-wrap gap-1.5" aria-hidden="true">
                    {build.map((p, i) => (
                      <span
                        key={p.seq}
                        title={`Plan ${p.seq} · ${p.state}`}
                        className={`w-4 h-4 rounded-[4px] border transition-colors duration-300 ${
                          i < done
                            ? p.plan_specific > 0
                              ? 'bg-v2accent border-v2accent'
                              : drafted(p) > 0
                                ? 'bg-v2accent-soft border-v2accent border-dashed border-2'
                                : 'bg-v2accent-soft border-v2accent'
                            : i === current && !finished
                              ? 'bg-paper border-ink'
                              : 'bg-paper border-v2border'
                        }`}
                      />
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-ink-500">
                    <span className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1">
                        <span className="w-3 h-3 rounded-[3px] bg-v2accent" /> plan-specific rules
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="w-3 h-3 rounded-[3px] bg-v2accent-soft border-2 border-dashed border-v2accent" /> drafted, awaiting approval
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <span className="w-3 h-3 rounded-[3px] bg-v2accent-soft border border-v2accent" /> shared rules only
                      </span>
                    </span>
                    {finished && !reduced && (
                      <button
                        type="button"
                        onClick={() => setRun((r) => r + 1)}
                        className="inline-flex items-center gap-1 font-semibold text-ink hover:text-v2accent"
                      >
                        <RotateCcw className="w-3.5 h-3.5" aria-hidden="true" />
                        Replay
                      </button>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <p className="text-ink-500">
                {n(mp.count)} plans in {mp.states.join(', ') || '—'} have their rules held as data;{' '}
                {n(plansWithOwnRules(mp))} have rules of their own. The plan-by-plan replay loads from the platform.
              </p>
            )}
          </div>

          <aside className="rounded-2xl border border-v2border bg-surface p-6 lg:sticky lg:top-24 self-start">
            <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-1">Held as data</p>
            <Counter value={plansValue} label="plans with rules held as data" strong />
            <Counter value={rowsValue} label="rule rows" />
            <Counter
              value={specificValue}
              label="plans with rules of their own"
              sub={
                draftsSoFar > 0
                  ? `${n(draftsSoFar)} drafted by agents, awaiting approval; the rest carry shared and state rules`
                  : 'the rest carry shared and state rules'
              }
            />
            <Counter value={statesValue} label={total ? 'states built' : 'states'} />

            {queue && queue.medicaid_without_rules > 0 && (
              <div className={`mt-5 pt-4 border-t border-v2border transition-opacity duration-500 ${finished || reduced || !total ? 'opacity-100' : 'opacity-40'}`}>
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-500 mb-1">Next for the agents</p>
                <p className="text-2xl font-bold text-ink tabular-nums">{n(queue.medicaid_without_rules)}</p>
                <p className="text-sm text-ink-700 mb-3">Medicaid payers in the national directory without rules yet</p>
                <div className="flex flex-wrap gap-1.5">
                  {queue.by_state.slice(0, 6).map((q) => (
                    <span key={q.state} className="text-xs px-2 py-0.5 rounded-full bg-paper border border-v2border text-ink-700 tabular-nums">
                      {q.state} {n(q.count)}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-4 text-xs text-ink-500">
              {coverage.rule_proposals.pending > 0
                ? `${n(coverage.rule_proposals.pending)} agent-drafted rules are waiting for a person to approve them.`
                : 'No agent-drafted rules are waiting for approval right now.'}{' '}
              {live ? 'Counts are live from the platform.' : 'Live counts load from the platform.'}
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default InterfaceBuilder;
